import classnames from "classnames";
import Cleave from "cleave.js";
import PropTypes from "prop-types";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";

import Icon from "../../Icon";
import { FormControl } from "../Form";
import FormHelper from "../FormHelper";
import FormInput from "../FormInput";
import Label from "../Label";

import "./textInput.scss";

const TextInput = ({
  name,
  label,
  options,
  autoComplete,
  helperText,
  className,
  isGroupFieldInput,
  groupFieldIndex,
  groupFieldName,
  shouldRevalidate,
  isIconLock,
}) => {
  const {
    register,
    control,
    trigger,
    clearErrors,
    formState: { errors },
  } = useFormContext();
  const currentValue = useWatch({
    control,
    name,
  });
  const { update } = useFieldArray({ name: groupFieldName });
  const [isFocused, setIsFocused] = useState(false);

  const isMasked = options?.masking;
  let hasValue = typeof currentValue !== "undefined" && currentValue !== "";
  let currentError = errors[name];

  const formattedInputClassName = isGroupFieldInput
    ? name.replace(
        `${groupFieldName}[${groupFieldIndex}].`,
        `${groupFieldName}${groupFieldIndex}-`
      )
    : name;

  if (isGroupFieldInput && errors[groupFieldName]?.[groupFieldIndex]) {
    currentError =
      errors[groupFieldName]?.[groupFieldIndex]?.[
        name.replace(`${groupFieldName}[${groupFieldIndex}].`, "")
      ];
  }

  const cleave = useRef(null);

  const handleFocus = () => {
    setIsFocused(!isFocused);
  };

  const handleBlur = (e) => {
    setIsFocused(false);
    const value =
      e.target.value ||
      (typeof currentValue !== "number" ? currentValue?.trim() : currentValue);
    hasValue = value !== "";

    if (
      cleave.current?.element?.value === "$" ||
      cleave.current?.element?.value === "%"
    ) {
      cleave.current.element.value = "";
    }

    update(name, value);
  };

  useEffect(() => {
    if (isGroupFieldInput && shouldRevalidate) {
      trigger(name);
    }
  }, [currentValue, isGroupFieldInput, shouldRevalidate, trigger, name]);

  useLayoutEffect(() => {
    if (isMasked) {
      cleave.current = new Cleave(`.text-input--${formattedInputClassName}`, {
        ...options?.masking,
      });
    }
  }, [formattedInputClassName, isMasked, options]);

  return (
    <FormControl
      className={classnames({
        "is-hidden": options?.hidden,
        "is-invisible": options?.invisible,
      })}
    >
      <FormInput
        type="text"
        className={classnames(className, {
          "has-value": hasValue,
          "has-error": (currentError && shouldRevalidate) || currentError,
          "is-active": !options?.readOnly && isFocused,
          "is-disabled": options?.disabled,
        })}
      >
        <Label
          htmlFor={name}
          text={label}
          required={options?.required}
          showRequiredAsterisk={options?.showAsterisk}
          showRequiredLabel={!isGroupFieldInput}
        />
        <input
          id={name}
          className={classnames({
            [`text-input--${formattedInputClassName}`]: formattedInputClassName,
          })}
          type="text"
          autoComplete={autoComplete}
          disabled={options?.disabled}
          readOnly={options?.readOnly}
          aria-invalid={!!currentError}
          placeholder={options?.placeholder}
          maxLength={options?.maxLength?.value}
          onFocus={handleFocus}
          onKeyUp={(e) => {
            if (isMasked && e.target.value !== "" && !!currentError) {
              clearErrors(name);
            }
          }}
          {...register(name, { ...options, onBlur: handleBlur })}
        />

        {!isGroupFieldInput && (currentError || hasValue) && (
          <div className="form-validation-icons">
            {hasValue && !currentError && (
              <Icon
                name={isIconLock ? "lock-solid" : "system-checkmark"}
                theme={isIconLock ? "disabled" : "success"}
                size={isIconLock ? "small" : undefined}
              />
            )}
            {currentError && <Icon name="system-warning" theme="error" />}
          </div>
        )}
      </FormInput>

      {currentError && (
        <FormHelper
          helperText={helperText}
          error={currentError}
          label={label}
        />
      )}
    </FormControl>
  );
};

TextInput.propTypes = {
  name: PropTypes.string,
  label: PropTypes.string,
  options: PropTypes.object,
  autoComplete: PropTypes.string,
  helperText: PropTypes.string,
  className: PropTypes.string,
  isGroupFieldInput: PropTypes.bool,
  groupFieldIndex: PropTypes.number,
  groupFieldName: PropTypes.string,
  shouldRevalidate: PropTypes.bool,
  isIconLock: PropTypes.bool,
};

TextInput.defaultProps = {
  options: {},
  autoComplete: "off",
  groupFieldIndex: 0,
  groupFieldName: "",
  isIconLock: false,
};

export default TextInput;
